import React, { createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { useGetUserProfile, useLogin, useLogout } from '../api/auth/use-auth';
import {
    useCustomerLogin,
    useCustomerLogout,
    useCustomerRegister,
    useGetCustomerProfile,
} from '../api/auth/use-customer-auth';
import { User } from '../models/user';
import { CustomerAuthUser } from '../models/customer';

export type UserTypeFromSubdomain = 'trader' | 'crm' | 'admin';

const getUserTypeFromHostname = (): UserTypeFromSubdomain => {
    const hostname = window.location.hostname;

    if (hostname.startsWith('admin.')) {
        return 'admin';
    }

    if (hostname.startsWith('crm.')) {
        return 'crm';
    }

    return 'trader';
};

interface AuthContextType {
    user: User | CustomerAuthUser | null;
    detectedUserType: 'trader' | 'crm' | 'admin' | 'unknown';
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string, userType?: 'user' | 'customer') => Promise<void>;
    register: (
        name: string,
        email: string,
        password: string,
        country?: string,
        phone: string
    ) => Promise<void>;
    logout: () => void;
    isTrader: () => boolean;
    isCRM: () => boolean;
    isAdmin: () => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    // Detect user type from hostname
    const detectedUserType = getUserTypeFromHostname();
    const isTrader = () => detectedUserType === 'trader';
    const isCRM = () => detectedUserType === 'crm';
    const isAdmin = () => detectedUserType === 'admin';

    // Check if we're on an auth page to prevent unnecessary auth checks
    const isAuthPage = location.pathname.startsWith('/auth/');

    // User hooks (CRM/Admin) - only load if on CRM or Admin subdomain and not on auth pages
    const { mutate: userLoginMutation } = useLogin();
    const { mutate: userLogoutMutation } = useLogout();
    const { data: userData, isLoading: userLoading } = useGetUserProfile({
        enabled: !isTrader() && !isAuthPage,
    });

    // Customer hooks (Traders) - only load if on trader subdomain and not on auth pages
    const { mutate: customerLoginMutation } = useCustomerLogin();
    const { mutate: customerLogoutMutation } = useCustomerLogout();
    const { mutate: customerRegisterMutation } = useCustomerRegister();
    const { data: customerData, isLoading: customerLoading } = useGetCustomerProfile({
        enabled: isTrader() && !isAuthPage,
    });

    const authenticatedUser = userData?.data || customerData?.data || null;
    const isLoading = userLoading || customerLoading;

    // Helper function to get error message - simple: 4XX = incorrect credentials
    const getErrorMessage = (error: any): string => {
        // Check if error has response (axios error)
        const status = error?.response?.status;

        // If it's a 4XX error (client error), show incorrect credentials message
        if (status >= 400 && status < 500) {
            return 'Incorrect email or password';
        }

        // For other errors, show generic message
        return error?.message || 'Login failed';
    };

    const login = async (email: string, password: string) => {
        // Auto-detect user type from hostname if not provided
        const targetUserType = isTrader() ? 'customer' : 'user';

        return new Promise<void>((resolve, reject) => {
            if (targetUserType === 'customer') {
                customerLoginMutation(
                    { email, password },
                    {
                        onSuccess: () => {
                            enqueueSnackbar('Login successful!', {
                                variant: 'success',
                                autoHideDuration: 3000,
                            });

                            navigate('/');
                            resolve();
                        },
                        onError: (error: any) => {
                            // Simple: if 4XX status, show incorrect credentials
                            const status = error?.response?.status;
                            const message =
                                status >= 400 && status < 500
                                    ? 'Incorrect email or password'
                                    : error?.message || 'Login failed';
                            enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
                            reject(error);
                        },
                    }
                );
            } else {
                userLoginMutation(
                    { email, password },
                    {
                        onSuccess: () => {
                            enqueueSnackbar('Login successful!', {
                                variant: 'success',
                                autoHideDuration: 3000,
                            });

                            navigate('/');
                            resolve();
                        },
                        onError: (error: any) => {
                            // Simple: if 4XX status, show incorrect credentials
                            const status = error?.response?.status;
                            const message =
                                status >= 400 && status < 500
                                    ? 'Incorrect email or password'
                                    : error?.message || 'Login failed';
                            enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
                            reject(error);
                        },
                    }
                );
            }
        });
    };

    const register = async (
        name: string,
        email: string,
        password: string,
        country?: string,
        phone: string
    ) => {
        return new Promise<void>((resolve, reject) => {
            customerRegisterMutation(
                { name, email, password, password_confirmation: password, country, phone },
                {
                    onSuccess: async () => {
                        enqueueSnackbar('Registration successful! Welcome to Quantix Trading!', {
                            variant: 'success',
                            autoHideDuration: 3000,
                        });
                        // Invalidate and refetch customer profile to ensure it's loaded
                        queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
                        // Wait a bit for the profile to be fetched, then navigate
                        // Use window.location for full page reload to ensure clean state
                        setTimeout(() => {
                            window.location.href = '/';
                        }, 500);
                        resolve();
                    },
                    onError: (error: any) => {
                        const errorMessage = getErrorMessage(error);
                        enqueueSnackbar(errorMessage, {
                            variant: 'error',
                            autoHideDuration: 5000,
                        });
                        reject(error);
                    },
                }
            );
        });
    };

    const logout = () => {
        // Clear localStorage auth-related data
        localStorage.removeItem('leads-page-visible-columns');
        // Clear any other auth-related localStorage items if needed

        // Dispatch logout event for other contexts to listen
        window.dispatchEvent(new Event('logout'));

        if (isTrader()) {
            customerLogoutMutation(undefined, {
                onSuccess: () => {
                    // Clear query cache
                    queryClient.clear();
                    enqueueSnackbar('Logged out successfully', { variant: 'success' });
                    // Use window.location for complete page reload to clear all state
                    window.location.href = '/auth/login';
                },
                onError: (error: Error) => {
                    // Clear query cache even on error
                    queryClient.clear();
                    enqueueSnackbar('Logout failed: ' + error.message, { variant: 'error' });
                    // Still navigate to login even if logout API fails
                    window.location.href = '/auth/login';
                },
            });
        } else {
            userLogoutMutation(undefined, {
                onSuccess: () => {
                    // Clear query cache
                    queryClient.clear();
                    enqueueSnackbar('Logged out successfully', { variant: 'success' });
                    // Use window.location for complete page reload to clear all state
                    window.location.href = '/auth/login';
                },
                onError: (error: Error) => {
                    // Clear query cache even on error
                    queryClient.clear();
                    enqueueSnackbar('Logout failed: ' + error.message, { variant: 'error' });
                    // Still navigate to login even if logout API fails
                    window.location.href = '/auth/login';
                },
            });
        }
    };

    const value = {
        user: authenticatedUser,
        detectedUserType,
        isAuthenticated: !!authenticatedUser?.email,
        isLoading,
        login,
        register,
        logout,
        isTrader,
        isCRM,
        isAdmin,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
